import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { constants as destinationConstants } from '@eb/destination-utils';

import BusinessSvg from 'eventbrite_design_system/iconography/icons/Business';
import MusicNoteSvg from 'eventbrite_design_system/iconography/icons/MusicNote';
import MaskSvg from 'eventbrite_design_system/iconography/icons/Mask';
import BeakerSvg from 'eventbrite_design_system/iconography/icons/Beaker';
import ParkSvg from 'eventbrite_design_system/iconography/icons/Park';
import CocktailSvg from 'eventbrite_design_system/iconography/icons/Cocktail';
import FilmCameraSvg from 'eventbrite_design_system/iconography/icons/FilmCamera';
import ShoeSvg from 'eventbrite_design_system/iconography/icons/Shoe';
import HeartSvg from 'eventbrite_design_system/iconography/icons/Heart';
import TravelSvg from 'eventbrite_design_system/iconography/icons/Travel';
import CharitySvg from 'eventbrite_design_system/iconography/icons/Charity';
import ReligionSvg from 'eventbrite_design_system/iconography/icons/Religion';
import RockingHorseSvg from 'eventbrite_design_system/iconography/icons/RockingHorse';
import HolidaySvg from 'eventbrite_design_system/iconography/icons/Holiday';
import BookSvg from 'eventbrite_design_system/iconography/icons/Book';
import MegaphoneSvg from 'eventbrite_design_system/iconography/icons/Megaphone';
import FashionSvg from 'eventbrite_design_system/iconography/icons/Fashion';
import HouseSvg from 'eventbrite_design_system/iconography/icons/House';
import AutoSvg from 'eventbrite_design_system/iconography/icons/Auto';
import GameSvg from 'eventbrite_design_system/iconography/icons/Game';
import StarSvg from 'eventbrite_design_system/iconography/icons/Star';
import LogoInstagramSvg from 'eventbrite_design_system/iconography/icons/LogoInstagram';
import LogoTwitterSvg from 'eventbrite_design_system/iconography/icons/LogoTwitter';
import LogoLinkedinSvg from 'eventbrite_design_system/iconography/icons/LogoLinkedin';
import LogoFacebookSvg from 'eventbrite_design_system/iconography/icons/LogoFacebook';
import LogoPinterestSvg from 'eventbrite_design_system/iconography/icons/LogoPinterest';
import LogoGooglePlusSvg from 'eventbrite_design_system/iconography/icons/LogoGooglePlus';
import LinkChunkySvg from 'eventbrite_design_system/iconography/icons/LinkChunky';

export const {
    EVENTBRITE,
    TICKETFLY,
    GOLDSTAR,
    LINK_FOLLOW_WHITELIST,
    EVENT_TYPE,
    EVENTS_TYPE,
    ALL_EVENTS_TYPE,
    GOOGLE_MAPS_CLIENT_KEY,
    EVENT_CARD_TYPE,
    ARTICLE_CARD_TYPE,
    PROFILE_CARD_TYPE,
    CATEGORY_TYPE,
    NEARBY_CITY_TYPE,
    TRENDING_SEARCH_TYPE,
    ENTITY_TYPE_MAP,
    SINGULARIZED_ENTITY_TYPE_MAP,
    SEARCH_CRITERIA_TYPE_MAP,
    SEARCH_TYPE_MAP,
    START_DATE,
    END_DATE,
    EVENT_PAGE_PARAM,
    SEARCH_TYPE_PAGE_PARAM_MAP,
    EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    OTHER_EVENTBRITE_CATEGORY,
    CATEGORY_LONG_NAME_ICON_MAP,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_CATEGORY_MAPPING_SEARCH,
    EVENTBRITE_CATEGORY_LOWERCASE_MAPPING,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    CURRENT_FUTURE,
    CUSTOM_DATE,
    DATES_MAPPING,
    DATES_MAPPING_SEARCH,
    DATES_UPPERCASE_MAPPING,
    DATES_LOWERCASE_MAPPING,
    DATES_TO_REFINEMENT,
    FORMAT_TAG_TYPE,
    CATEGORY_TAG_TYPE,
    EVENTBRITE_FORMAT_MAPPING,
    EVENTBRITE_FORMAT_MAPPING_SEARCH,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    PRICE_MAPPING,
    QUALITY,
    FORMATTED_CATEGORY_MAPPING,
    FORMATTED_DATE_MAPPING,
    FORMATTED_FORMAT_MAPPING,
    FORMATTED_PRICE_MAPPING,
    EVENT_PROPTYPES,
    BRANDING_PROPTYPE,
    PLACE_OBJECT_PROPTYPE,
    SERVER_SUGGESTIONS_PROPTYPE,
    SUGGESTIONS_PROPTYPE,
    LOCATION_SUGGESTION_PROPTYPE,
    BUCKET_API_RESPONSE_PROPTYPE,
    SEARCH_API_RESPONSE_PROPTYPE,
    SHARE_OPTIONS_PROPTYPE,
    ARTIST_SOCIAL_LINKS_PROPTYPE,
    DEFAULT_BLURB_NO_BUCKETS,
    DEFAULT_BLURB_NO_EVENTS,
    getDefaultBlurb,
    EVENT_SAVE,
    EVENT_SAVE_ERROR,
    CITY_NAME_SHORT_CUT,
    CITY_NAME_DEFAULT_CUT,
    CITY_GRADIENTS,
    SHORT_STATES,
    AUTOCOMPLETE_STORAGE_KEY,
    LOCATION_STORAGE_KEY,
    EVENT_COOKIE,
    CITY_CUTOFF,
    COLLECTION_TYPE,
} = destinationConstants;

const {
    BUSINESS,
    SCIENCE,
    MUSIC,
    FILM,
    PERFORMING,
    FASHION,
    HEALTH,
    SPORTS,
    TRAVEL,
    FOOD,
    CHARITY,
    POLITICS,
    COMMUNITY,
    RELIGION,
    FAMILY_EDUCATION,
    SEASONAL,
    HOME_LIFESTYLE,
    AUTO_BOAT_AIR,
    HOBBIES,
    SCHOOL,
    OTHER,
} = destinationConstants;

export * from './filters';
export * from './analytics';

export const MOBILE_BREAKPOINT = '822px';

/****************************** APP LEVEL ********************************/

export const LUX_LABEL_SEARCH = 'Search';
export const LUX_LABEL_BROWSE = 'Browse';

export const LUX_MARK_BROWSE_READY = 'browse-page-ready';
export const LUX_MARK_SEARCH_READY = 'search-page-ready';
export const LUX_MARK_SEARCH_QUERIED = 'search-query-sent';
export const LUX_MARK_SEARCH_RENDERED = 'search-query-rendered';
export const LUX_MEASURE_SEARCH_RESPONSE_TIME = 'search-query-response-time';
export const LUX_MARK_MAP_RENDERED = 'search-map-rendered';

/****************************** SHARED CLASSES ********************************/

export const SHARED_BUCKET_SPACING_CLASSES = classNames(
    'content-bucket',
    'eds-l-pad-vert-20',
    'eds-l-sw-pad-vert-16',
    'eds-l-sm-pad-vert-16',
    'eds-l-sn-pad-vert-16',
);

export const NON_EVENT_BUCKET_SPACING_CLASSES = classNames(
    'content-bucket',
    'eds-l-pad-top-16',
    'eds-l-sw-pad-vert-16',
    'eds-l-sm-pad-vert-16',
    'eds-l-sn-pad-vert-16',
);

export const BUCKET_WRAPPER_WITH_DECORATION_CLASSES = classNames(
    'eds-l-mar-top-5',
    'eds-l-sm-mar-top-16',
    'eds-l-sn-mar-top-16',
);

export const TAG_BACKGROUND_COLOR = 'eds-bg-color--ui-blue';
export const TAG_STYLE = 'outlined';

/****************************** TEMPLATES AND CARD TYPES ********************************/

export const ARTICLE_TEMPLATE = 'article';
export const CALENDAR_TEMPLATE = 'calendar';
export const FEATURED_TEMPLATE = 'featured';
export const CATEGORY_TEMPLATE = 'category';
export const MAP_TEMPLATE = 'map';
export const NEARBY_CITIES_TEMPLATE = 'nearby_place';
export const TRENDING_SEARCHES_TEMPLATE = 'trending_searches';
export const COLLECTION_TEMPLATE = 'collection';
export const ORGANIZER_FOLLOW_TEMPLATE = 'organizer_follow';

export const LARGE_CARD_TEMPLATE_3 = 'card_big_3';
export const LARGE_CARD_TEMPLATE_6 = 'card_big_6';
export const LIST_CARD_TEMPLATE = 'card_list';
export const CARD_TEMPLATE_4 = 'card_small_4';
export const CARD_TEMPLATE_8 = 'card_small_8';
export const CARD_BIG = 'card_big';

const THIS_WEEKEND_BUCKETS_KEY = 'this_weekend';
const POPULAR_EVENTS_BUCKET_KEY = 'popular_events';
const CURATED_EVENTS_BUCKET_KEY = 'best_of_city_events';
const MUSIC_EVENTS_BUCKET_KEY = 'music_events';

export const EDITORIAL_ARTICLES_BUCKET_KEY = 'editorial_articles';
export const FREE_EVENTS_BUCKET_KEY = 'free_events';

/****************************** URLS ********************************/

export const BASE_URL = '/d';
export const BROWSE_URL = ':location/events/';
export const SEARCH_URL = ':location/:refinementOne(/:refinementTwo)/';

export const SEARCH_RELEVANT_QUERY_PARAMS = [
    START_DATE,
    END_DATE,
    EVENT_PAGE_PARAM,
    'src',
    'bbox',
    'tags',
    'cur',
    'lang',
];

/****************************** CATEGORY ID MAPPING ********************************/

//Very TBD, will be replaced once official
//category graphics are put together.
export const CATEGORY_NAME_ICON_MAP = {
    [BUSINESS]: <BusinessSvg />,
    [MUSIC]: <MusicNoteSvg />,
    [PERFORMING]: <MaskSvg />,
    [SCIENCE]: <BeakerSvg />,
    [COMMUNITY]: <ParkSvg />,
    [FOOD]: <CocktailSvg />,
    [FILM]: <FilmCameraSvg />,
    [SPORTS]: <ShoeSvg />,
    [HEALTH]: <HeartSvg />,
    [TRAVEL]: <TravelSvg />,
    [CHARITY]: <CharitySvg />,
    [RELIGION]: <ReligionSvg />,
    [FAMILY_EDUCATION]: <RockingHorseSvg />,
    [SEASONAL]: <HolidaySvg />,
    [SCHOOL]: <BookSvg />,
    //Backup: 'trash'
    [POLITICS]: <MegaphoneSvg />,
    [FASHION]: <FashionSvg />,
    [HOME_LIFESTYLE]: <HouseSvg />,
    [AUTO_BOAT_AIR]: <AutoSvg />,
    [HOBBIES]: <GameSvg />,
    [OTHER]: <StarSvg />,
};

export const CATEGORY_DROPDOWN_SELECT = 'category_select';

/****************************** JANUS CONSTANTS ********************************/

export const AGGREGATION_EXPERIMENT = 'exp_eb_81052_la_aggregation';

export const CITY_BROWSE_EVENT_CLICKED = 'city_browse_event_clicked';
export const CITY_BROWSE_MANUAL_EVENT_CLICKED =
    'city_browse_manual_event_clicked';
export const CITY_BROWSE_EVENT_BOOKMARKED = 'city_browse_event_bookmarked';

export const DESTINATION_SEARCH_EVENT_BOOKMARKED_CHECKPOINT =
    'destination_search_event_bookmarked';
export const DESTINATION_SEARCH_EVENT_CLICKED_CHECKPOINT =
    'destination_search_event_clicked';
export const DESTINATION_MANUAL_SEARCH_EVENT_CLICKED_CHECKPOINT =
    'destination_search_manual_event_clicked';

export const SEARCH_TAG_CLICKED = 'search_tag_clicked';
export const SEARCH_TAG_CLEARED = 'search_tag_cleared';

export const NON_MANUAL_SOURCES = [TICKETFLY, EVENTBRITE, GOLDSTAR];
export const AGG_EXPERIMENT_LA_METRO_WHITELIST = [
    'ca--los-angeles',
    'ca--west-hollywood',
    'ca--santa-monica',
    'ca--long-beach',
    'ca--burbank',
    'ca--beverly-hills',
    'ca--riverside',
    'ca--glendale',
    'ca--culver-city',
    'ca--marina-del-rey',
];

export const EXPERIMENTS_SHAPE_PROP_TYPE = PropTypes.shape({
    event_cards: PropTypes.string,
});

/****************************** IMAGES ********************************/

export const DECORATIVE_IMAGE_PLACEMENT_MAP = [
    {
        selectors: [POPULAR_EVENTS_BUCKET_KEY, CURATED_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationTopRightUrl',
        position: 'right',
        classes: 'decorative-image--one',
        parentClasses: 'eds-l-pad-top-6',
    },
    {
        selectors: [THIS_WEEKEND_BUCKETS_KEY],
        urlSelector: 'decorationTopLeftUrl',
        position: 'left',
        classes: 'decorative-image--two',
        parentClasses: 'eds-l-pad-top-12',
    },
    {
        selectors: [EDITORIAL_ARTICLES_BUCKET_KEY, MUSIC_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationBottomRightUrl',
        position: 'right',
        classes: 'decorative-image--three',
        parentClasses: 'eds-l-pad-top-6',
    },
    {
        selectors: [FREE_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationBottomLeftUrl',
        position: 'left',
        classes: 'decorative-image--four',
        parentClasses: 'eds-l-pad-top-6',
    },
];

/****************************** Artist Social Link Tye Constants ********************************/

export const SOCIAL_TYPE_ICON_MAP = {
    instagram: <LogoInstagramSvg />,
    twitter: <LogoTwitterSvg />,
    linkedin: <LogoLinkedinSvg />,
    facebook: <LogoFacebookSvg />,
    pinterest: <LogoPinterestSvg />,
    'google-plus': <LogoGooglePlusSvg />,
    web: <LinkChunkySvg />,
};

export const HEADER_HEIGHT = 72;
